<template>
  <div class="page-view">

    <!-- Header -->
    <BasePageHeader :title="projectName || $t('projectItems.plannedItems')" @back="$router.back()">
      <template #actions>
        <button class="btn ghost small" @click="startAddSection">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 shrink-0">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          {{ $t('projectItems.section') }}
        </button>
        <button class="btn primary small" @click="browseAndAdd">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 shrink-0">
            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4"/><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
          </svg>
          {{ $t('projectItems.browseAndAdd') }}
        </button>
        <button class="btn ghost small" @click="browseBundles">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 shrink-0">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
          </svg>
          {{ $t('projectItems.bundles') }}
        </button>
        <button class="btn primary small" @click="goToPick">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 shrink-0">
            <polyline points="9 11 12 14 22 4"/>
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
          </svg>
          {{ $t('projectItems.startPicking') }}
        </button>
      </template>
    </BasePageHeader>

    <!-- Scan / add item bar -->
    <div class="relative px-4 pt-4 pb-1">
      <div class="flex gap-2 items-start">
        <BaseFormField density="compact" class="flex-1 !mb-0">
          <BaseInput
            ref="addItemInput"
            density="compact"
            v-model="addItemQuery"
            :placeholder="activeSectionId != null ? `${$t('projectItems.addToSectionPlaceholder')} &quot;${activeSectionName}&quot;…` : $t('projectItems.addSearchPlaceholder')"
            :disabled="addingItem"
            :aria-label="$t('projectItems.addSearchPlaceholder')"
            autocomplete="off"
            @keydown.enter="onAddItem"
            @focus="searchOpen = true"
            @blur="onAddBlur"
          />
        </BaseFormField>
        <BaseFormField v-if="sections.length > 0" density="compact" class="!mb-0" style="min-width:0; width:auto; max-width:9rem;">
          <BaseSelect
            density="compact"
            :model-value="activeSectionId ?? ''"
            :aria-label="$t('ui.addToSection')"
            :title="$t('ui.addToSection')"
            @update:model-value="activeSectionId = $event === '' ? null : Number($event)"
          >
            <option value="">{{ $t('projectItems.noSection') }}</option>
            <option v-for="s in sections" :key="s.id" :value="s.id">{{ s.name }}</option>
          </BaseSelect>
        </BaseFormField>
        <button
          class="btn primary small"
          :disabled="!addItemQuery.trim() || addingItem"
          @click="onAddItem"
        >{{ addingItem ? '…' : $t('common.add') }}</button>
      </div>

      <div v-if="addItemError" class="text-xs text-red-400 mt-1">{{ addItemError }}</div>

      <!-- Live search dropdown -->
      <div
        v-if="searchOpen && addItemQuery.trim().length >= 2 && (searchLoading || searchResults.length > 0)"
        class="absolute left-4 right-4 top-full bg-[var(--color-surface)] border border-white/10 rounded-xl shadow-2xl z-50 max-h-72 overflow-y-auto"
      >
        <div v-if="searchLoading" class="py-3 px-4 text-center text-[var(--color-muted)] text-sm">{{ $t('projectItems.searchingItems') }}</div>
        <template v-else>
          <button
            v-for="result in searchResults"
            :key="result.resultType + '-' + result.id"
            class="block w-full bg-transparent border-none border-b border-[var(--color-border)] last:border-b-0 px-3.5 py-2.5 text-left cursor-pointer text-[var(--color-text)] hover:bg-[var(--color-surface-2)] transition-colors"
            @mousedown.prevent="addFromSearch(result)"
          >
            <div class="text-sm font-semibold">{{ result.displayName }}</div>
            <div class="flex items-center gap-1.5 mt-0.5 text-xs text-[var(--color-muted)]">
              <span v-if="result.sub">{{ result.sub }}</span>
              <span class="badge badge-muted ml-auto">{{ result.typeLabel }}</span>
            </div>
          </button>
        </template>
      </div>
    </div>

    <!-- Availability warnings (shown after any item/bundle add that has open-ended checkout) -->
    <AvailabilityWarningBanner
      :warnings="openEndedWarnings"
      @dismiss="openEndedWarnings = []"
    />

    <!-- Add section inline form -->
    <div v-if="addingSectionInline" class="flex gap-2 items-center px-4 pt-2 pb-3">
      <BaseFormField density="compact" class="flex-1 !mb-0">
        <BaseInput
          ref="newSectionInput"
          v-model="newSectionName"
          density="compact"
          :placeholder="$t('projectItems.sectionNamePlaceholder')"
          :aria-label="$t('projectItems.sectionNamePlaceholder')"
          maxlength="100"
          :disabled="creatingSection"
          @keydown.enter="createSection"
          @keydown.escape="addingSectionInline = false"
        />
      </BaseFormField>
      <button class="btn primary small" :disabled="!newSectionName.trim() || creatingSection" @click="createSection">
        {{ creatingSection ? '…' : $t('common.create') }}
      </button>
      <button class="btn ghost small" @click="addingSectionInline = false">{{ $t('common.cancel') }}</button>
    </div>

    <!-- Loading / error -->
    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />

    <template v-else>

      <!-- Discount badge -->
      <div v-if="discountPercentage && parseFloat(discountPercentage) > 0" class="flex items-center gap-1.5 mx-4 mt-2 px-3 py-2 bg-stone-900 border border-orange-900 rounded-lg text-orange-400 text-xs font-semibold">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 shrink-0">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
        </svg>
        <span class="text-orange-400">{{ $t('projectItems.projectDiscount') }} {{ parseFloat(discountPercentage) }}%</span>
      </div>

      <!-- Empty state when no sections and no items -->
      <EmptyState v-if="!sections.length && !projectItems.length && !bundleGroups.length" :message="$t('projectItems.noItemsYet')" />

      <!-- Sections -->
      <div v-for="section in sections" :key="section.id" class="mt-4">
        <ProjectSectionPanel
          :section="section"
          :item-count="(activeItemsBySection[section.id]?.length ?? 0) + (bundleGroupsBySection[section.id]?.reduce((s, g) => s + g.items.length, 0) ?? 0)"
          @rename="onRenameSection"
          @delete="openDeleteSection"
          @browse="onBrowseSection"
        >
          <!-- Bundle checkout groups within this section (card, sits inside list-stack) -->
          <BundleGroupPanel
            v-for="group in bundleGroupsBySection[section.id] ?? []"
            v-show="group.items.length > 0"
            :key="`bg-${group.id}`"
            :group="group"
          >
            <div
              v-for="item in group.items"
              :key="item.id"
              class="data-card"
              :class="{ 'opacity-40 pointer-events-none': item._removing }"
            >
              <ProjectItemCard
                :item="item"
                :editing-qty-id="editingQtyId"
                :editing-qty-val="editingQtyVal"
                :editing-price-id="editingPriceId"
                :editing-price-val="editingPriceVal"
                :sections="sections"
                :effective-usage-days="effectiveUsageDays"
                :discount-percentage="discountPercentage"
                @edit-qty="onEditQty"
                @save-qty="onSaveQty"
                @cancel-qty="editingQtyId = null"
                @edit-price="onEditPrice"
                @save-price="onSavePrice"
                @cancel-price="editingPriceId = null"
                @remove="onRemoveItem"
                @move="onMoveItem"
                @navigate="onNavigateItem"
                @update:editing-qty-val="editingQtyVal = $event"
                @update:editing-price-val="editingPriceVal = $event"
              />
            </div>
          </BundleGroupPanel>

          <!-- Regular items in this section -->
          <EmptyState
            v-if="!activeItemsBySection[section.id]?.length && !(bundleGroupsBySection[section.id]?.some(g => g.items.length > 0))"
            :message="$t('projectItems.noItemsInSection')"
          />
          <div
            v-for="item in activeItemsBySection[section.id]"
            :key="item.id"
            class="data-card"
            :class="{ 'opacity-40 pointer-events-none': item._removing }"
          >
            <ProjectItemCard
              :item="item"
              :editing-qty-id="editingQtyId"
              :editing-qty-val="editingQtyVal"
              :editing-price-id="editingPriceId"
              :editing-price-val="editingPriceVal"
              :sections="sections"
              :effective-usage-days="effectiveUsageDays"
              :discount-percentage="discountPercentage"
              @edit-qty="onEditQty"
              @save-qty="onSaveQty"
              @cancel-qty="editingQtyId = null"
              @edit-price="onEditPrice"
              @save-price="onSavePrice"
              @cancel-price="editingPriceId = null"
              @remove="onRemoveItem"
              @move="onMoveItem"
              @navigate="onNavigateItem"
              @update:editing-qty-val="editingQtyVal = $event"
              @update:editing-price-val="editingPriceVal = $event"
            />
          </div>
        </ProjectSectionPanel>
      </div>

      <!-- Ungrouped items (no section) -->
      <template v-if="ungroupedItems.length || ungroupedBundleGroups.some(g => g.items.length > 0)">
        <div class="flex items-center gap-2 px-4 pt-4 pb-1">
          <span class="section-label">{{ $t('projectItems.ungrouped') }}</span>
          <span class="badge badge-muted badge-pill">{{
            ungroupedItems.length + ungroupedBundleGroups.reduce((s, g) => s + g.items.length, 0)
          }}</span>
        </div>

        <!-- Bundle groups and regular items share the same list-stack -->
        <div class="list-stack">
          <BundleGroupPanel
            v-for="group in ungroupedBundleGroups"
            v-show="group.items.length > 0"
            :key="`bg-${group.id}`"
            :group="group"
          >
            <div
              v-for="item in group.items"
              :key="item.id"
              class="data-card"
              :class="{ 'opacity-40 pointer-events-none': item._removing }"
            >
              <ProjectItemCard
                :item="item"
                :editing-qty-id="editingQtyId"
                :editing-qty-val="editingQtyVal"
                :editing-price-id="editingPriceId"
                :editing-price-val="editingPriceVal"
                :sections="sections"
                :effective-usage-days="effectiveUsageDays"
                :discount-percentage="discountPercentage"
                @edit-qty="onEditQty"
                @save-qty="onSaveQty"
                @cancel-qty="editingQtyId = null"
                @edit-price="onEditPrice"
                @save-price="onSavePrice"
                @cancel-price="editingPriceId = null"
                @remove="onRemoveItem"
                @move="onMoveItem"
                @navigate="onNavigateItem"
                @update:editing-qty-val="editingQtyVal = $event"
                @update:editing-price-val="editingPriceVal = $event"
              />
            </div>
          </BundleGroupPanel>

          <div
            v-for="item in ungroupedItems"
            :key="item.id"
            class="data-card"
            :class="{ 'opacity-40 pointer-events-none': item._removing }"
          >
            <ProjectItemCard
              :item="item"
              :editing-qty-id="editingQtyId"
              :editing-qty-val="editingQtyVal"
              :editing-price-id="editingPriceId"
              :editing-price-val="editingPriceVal"
              :sections="sections"
              :effective-usage-days="effectiveUsageDays"
              :discount-percentage="discountPercentage"
              @edit-qty="onEditQty"
              @save-qty="onSaveQty"
              @cancel-qty="editingQtyId = null"
              @edit-price="onEditPrice"
              @save-price="onSavePrice"
              @cancel-price="editingPriceId = null"
              @remove="onRemoveItem"
              @move="onMoveItem"
              @navigate="onNavigateItem"
              @update:editing-qty-val="editingQtyVal = $event"
              @update:editing-price-val="editingPriceVal = $event"
            />
          </div>
        </div>
      </template>

    </template>

    <!-- Delete section modal -->
    <div v-if="deletingSection" class="modal-overlay" @click.self="deletingSection = null">
      <div class="confirm-modal">
        <div class="confirm-modal__title">{{ $t('common.delete') }} "{{ deletingSection.name }}"?</div>
        <div class="confirm-modal__body">
          <p v-if="(deletingSection.project_items_count ?? 0) > 0">
            {{ $t('projectItems.sectionActiveItems', { count: deletingSection.project_items_count }) }}
          </p>
          <BaseFormField :label="$t('projectItems.moveItemsTo')" class="mt-3">
            <BaseSelect
              :model-value="deleteMoveTarget ?? ''"
              @update:model-value="deleteMoveTarget = $event === '' ? null : Number($event)"
            >
              <option value="">{{ $t('projectItems.leaveUngrouped') }}</option>
              <option v-for="s in otherSections" :key="s.id" :value="s.id">{{ s.name }}</option>
            </BaseSelect>
          </BaseFormField>
        </div>
        <div class="confirm-modal__actions">
          <button class="confirm-btn cancel" :disabled="deleteSectionLoading" @click="deletingSection = null">{{ $t('common.cancel') }}</button>
          <button class="confirm-btn delete" :disabled="deleteSectionLoading" @click="confirmDeleteSection">
            {{ deleteSectionLoading ? '…' : $t('common.delete') }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { useStore } from '@/stores/store'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import ProjectSectionPanel from '@/components/ProjectSectionPanel.vue'
import ProjectItemCard from '@/components/ProjectItemCard.vue'
import BundleGroupPanel from '@/components/BundleGroupPanel.vue'
import AvailabilityWarningBanner from '@/components/AvailabilityWarningBanner.vue'
import {
  getProjectItemsGrouped,
  getProjectSections,
  createProjectSection,
  updateProjectSection,
  deleteProjectSection,
  moveProjectItemToSection,
  addAdhocItem,
  removeProjectItem,
  updateProjectItem,
  getSearchResults,
  addItemToProjectById,
  addUnresolvedMultiItemToProject,
  parseBundleCheckoutError,
  getOpenEndedCheckoutsForInstance,
  getActiveCheckouts,
} from '@/api/api.js'

const store  = useStore()
const router = useRouter()
const route  = useRoute()
const toast  = useToast()
const { t } = useI18n()

const projectId   = computed(() => route.params.projectId)
const projectName = ref('')
const projectStartTime    = ref(null)
const projectEndTime      = ref(null)
const discountPercentage  = ref(null)
const effectiveUsageDays  = ref(null)

// ─── items & sections ──────────────────────────────────────────────────────────
const projectItems = ref([])   // ungrouped items (feeds section logic unchanged)
const bundleGroups = ref([])   // bundle checkout groups
const sections     = ref([])
const loading      = ref(false)
const loadError    = ref(null)

// ─── search/add bar ────────────────────────────────────────────────────────────
const addItemQuery   = ref('')
const addingItem     = ref(false)
const addItemError   = ref(null)
const addItemInput   = ref(null)
const searchResults  = ref([])
const searchOpen     = ref(false)
const searchLoading  = ref(false)
let   searchDebounce = null

// Section to target when adding items (null = ungrouped)
const activeSectionId = ref(null)
const activeSectionName = computed(() =>
  sections.value.find(s => s.id === activeSectionId.value)?.name ?? null
)

// ─── section CRUD state ────────────────────────────────────────────────────────
const addingSectionInline  = ref(false)
const newSectionName       = ref('')
const newSectionInput      = ref(null)
const creatingSection      = ref(false)
const deletingSection      = ref(null)
const deleteMoveTarget     = ref(null)
const deleteSectionLoading = ref(false)

const otherSections = computed(() =>
  sections.value.filter(s => s.id !== deletingSection.value?.id)
)

// ─── availability warnings ────────────────────────────────────────────────────
const openEndedWarnings = ref([])

/**
 * After adding item_metadata instances to the plan, check each for active
 * checkouts with no expected return date and surface warnings.
 */
async function checkOpenEndedWarning(item_metadata_id, displayName) {
  try {
    const hits = await getOpenEndedCheckoutsForInstance(item_metadata_id)
    if (hits.length) {
      const c = hits[0]
      openEndedWarnings.value.push({
        instanceId: item_metadata_id,
        name: displayName,
        checkedOutBy: c.user ?? c.checked_out_by ?? null,
        projectName:  c.project ?? null,
      })
    }
  } catch {
    // non-blocking — ignore API errors for this check
  }
}

/**
 * After a bundle checkout, check the whole project for open-ended active
 * checkouts (no expected_checkin). Deduplicates against already-warned IDs.
 */
async function checkBundleOpenEndedWarnings() {
  try {
    const res = await getActiveCheckouts({
      project: parseInt(projectId.value, 10),
      expected_checkin__isnull: true,
      page_size: 50,
    })
    const checkouts = res.results ?? res ?? []
    const alreadyWarned = new Set(openEndedWarnings.value.map((w) => w.instanceId))
    for (const c of checkouts) {
      const mid = typeof c.item_metadata === 'object' ? c.item_metadata?.id : c.item_metadata
      if (mid && !alreadyWarned.has(mid)) {
        alreadyWarned.add(mid)
        const meta = typeof c.item_metadata === 'object' ? c.item_metadata : null
        const name = meta?.item?.name ?? meta?.assetTag ?? meta?.serial_number ?? `#${mid}`
        openEndedWarnings.value.push({
          instanceId: mid,
          name,
          checkedOutBy: c.user ?? c.checked_out_by ?? null,
          projectName:  c.project ?? null,
        })
      }
    }
  } catch {
    // non-blocking
  }
}

// ─── qty / price editing ───────────────────────────────────────────────────────
const editingQtyId    = ref(null)
const editingQtyVal   = ref('')
const editingPriceId  = ref(null)
const editingPriceVal = ref('')

// ─── grouping computed ─────────────────────────────────────────────────────────
const activeItemsBySection = computed(() => {
  const map = {}
  for (const item of projectItems.value) {
    const sid = item.section ?? null
    if (sid != null) {
      if (!map[sid]) map[sid] = []
      map[sid].push(item)
    }
  }
  return map
})

const ungroupedItems = computed(() =>
  projectItems.value.filter(item => (item.section ?? null) === null)
)

// Bundle groups sorted into sections based on items[0].section
const bundleGroupsBySection = computed(() => {
  const map = {}
  for (const group of bundleGroups.value) {
    const sectionId = group.items[0]?.section ?? null
    if (sectionId != null) {
      if (!map[sectionId]) map[sectionId] = []
      map[sectionId].push(group)
    }
  }
  return map
})

const ungroupedBundleGroups = computed(() =>
  bundleGroups.value.filter(g => (g.items[0]?.section ?? null) === null)
)

// ─── load ──────────────────────────────────────────────────────────────────────
async function load() {
  loading.value   = true
  loadError.value = null
  try {
    const [grouped, sectionsRes] = await Promise.all([
      getProjectItemsGrouped(projectId.value),
      getProjectSections(projectId.value),
    ])
    const project = grouped.project
    projectName.value        = project.name ?? ''
    projectStartTime.value   = project.start_time ?? null
    projectEndTime.value     = project.end_time ?? null
    bundleGroups.value       = grouped.bundle_groups
    projectItems.value       = grouped.ungrouped_list
    discountPercentage.value = grouped.discount_percentage
    effectiveUsageDays.value = grouped.effective_usage_days
    sections.value           = sectionsRes.results ?? sectionsRes ?? []
  } catch {
    loadError.value = t('projectItems.loadFailed')
  } finally {
    loading.value = false
  }
}

// ─── section CRUD ──────────────────────────────────────────────────────────────
async function startAddSection() {
  addingSectionInline.value = true
  newSectionName.value = ''
  await nextTick()
  newSectionInput.value?.focus()
}

async function createSection() {
  const name = newSectionName.value.trim()
  if (!name || creatingSection.value) return
  creatingSection.value = true
  try {
    const s = await createProjectSection(projectId.value, name)
    sections.value.push(s)
    newSectionName.value      = ''
    addingSectionInline.value = false
    toast.success(t('projectItems.sectionCreated'))
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.data?.name?.[0] ?? e?.message ?? t('projectItems.sectionCreateFailed'))
  } finally {
    creatingSection.value = false
  }
}

async function onRenameSection({ section, name }) {
  try {
    const updated = await updateProjectSection(section.id, { name })
    const idx = sections.value.findIndex(s => s.id === section.id)
    if (idx !== -1) sections.value[idx] = { ...sections.value[idx], ...updated }
    projectItems.value.forEach(item => {
      if (item.section === section.id) item.section_name = updated.name ?? name
    })
    toast.success(t('projectItems.sectionRenamed'))
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.data?.name?.[0] ?? e?.message ?? t('projectItems.sectionRenameFailed'))
  }
}

function openDeleteSection(section) {
  deletingSection.value  = section
  deleteMoveTarget.value = null
}

async function confirmDeleteSection() {
  if (!deletingSection.value) return
  deleteSectionLoading.value = true
  const sectionId  = deletingSection.value.id
  const moveTarget = deleteMoveTarget.value
  try {
    await deleteProjectSection(sectionId, { move_items_to: moveTarget })
    sections.value = sections.value.filter(s => s.id !== sectionId)
    // Optimistically reassign items client-side
    const targetName = moveTarget != null
      ? (sections.value.find(s => s.id === moveTarget)?.name ?? null)
      : null
    projectItems.value.forEach(item => {
      if (item.section === sectionId) {
        item.section      = moveTarget ?? null
        item.section_name = targetName
      }
    })
    if (activeSectionId.value === sectionId) activeSectionId.value = null
    deletingSection.value = null
    toast.success(t('projectItems.sectionDeleted'))
  } catch (e) {
    toast.error(e?.message ?? t('projectItems.sectionDeleteFailed'))
  } finally {
    deleteSectionLoading.value = false
  }
}

function onBrowseSection(section) {
  store.enableAddToProject({
    id:           parseInt(projectId.value, 10),
    name:         projectName.value,
    start_time:   projectStartTime.value,
    end_time:     projectEndTime.value,
    section_id:   section.id,
    section_name: section.name,
  })
  router.push('/items')
}

// ─── move item ─────────────────────────────────────────────────────────────────
async function onMoveItem(item, newSectionId) {
  const prevSection     = item.section
  const prevSectionName = item.section_name
  item.section      = newSectionId ?? null
  item.section_name = newSectionId != null
    ? (sections.value.find(s => s.id === newSectionId)?.name ?? null)
    : null
  try {
    await moveProjectItemToSection(item.id, newSectionId ?? null)
  } catch (e) {
    item.section      = prevSection
    item.section_name = prevSectionName
    const msg = e?.data?.detail ?? e?.data?.error ?? e?.message ?? t('ui.moveItemFailed')
    toast.error(msg)
  }
}

// ─── item editing ──────────────────────────────────────────────────────────────
function onEditPrice(item) {
  if (item._removing) return
  editingPriceId.value  = item.id
  editingPriceVal.value = item.price_override != null ? String(item.price_override) : ''
}

async function onSavePrice(item) {
  const raw = editingPriceVal.value.trim()
  editingPriceId.value = null
  const parsed = raw === '' ? null : parseFloat(raw)
  if (raw !== '' && isNaN(parsed)) return
  if (parsed === parseFloat(item.price_override ?? '') || (raw === '' && item.price_override == null)) return
  try {
    const updated = await updateProjectItem(item.id, { price_override: raw === '' ? null : String(parsed.toFixed(2)) })
    item.price_override       = updated?.price_override ?? (raw === '' ? null : String(parsed.toFixed(2)))
    item.effective_daily_rate = updated?.effective_daily_rate ?? item.effective_daily_rate
  } catch {
    toast.error(t('projectItems.priceFailed'))
  }
}

function onEditQty(item) {
  if (item._removing) return
  editingQtyId.value  = item.id
  editingQtyVal.value = String(item.planned_quantity ?? 1)
}

async function onSaveQty(item) {
  const qty = parseInt(editingQtyVal.value, 10)
  editingQtyId.value = null
  if (isNaN(qty) || qty < 1 || qty === item.planned_quantity) return
  const alreadyPacked = item.packed_quantity ?? 0
  if (alreadyPacked > 0 && qty < alreadyPacked) {
    toast.warning(t('ui.plannedBelowPacked', { count: alreadyPacked }))
  }
  try {
    await updateProjectItem(item.id, { quantity: qty })
    item.planned_quantity = qty
  } catch (err) {
    const data = err.data
    let msg = t('ui.updateQuantityFailed')
    if (data && typeof data === 'object') {
      const firstField = Object.values(data).find(v => Array.isArray(v) ? v.length : v)
      if (Array.isArray(firstField)) msg = firstField[0]
      else if (typeof firstField === 'string') msg = firstField
    }
    toast.error(msg)
  }
}

// ─── search watcher ────────────────────────────────────────────────────────────
watch(addItemQuery, (val) => {
  clearTimeout(searchDebounce)
  if (val.trim().length < 2) {
    searchResults.value = []
    searchLoading.value = false
    return
  }
  searchLoading.value = true
  searchDebounce = setTimeout(async () => {
    try {
      const q = val.trim()
      const res = await getSearchResults(q)
      const items = []
      ;(res.results?.item_metadata ?? []).forEach(r => {
        items.push({ id: r.object_id, resultType: 'item_metadata', displayName: r.display?.name ?? `#${r.object_id}`, sub: r.display?.subtitle || null, typeLabel: t('itemMetadata.title') })
      })
      ;(res.results?.item ?? []).forEach(r => {
        items.push({ id: r.object_id, resultType: 'item', displayName: r.display?.name ?? `#${r.object_id}`, sub: r.display?.subtitle || null, typeLabel: t('onboarding.itemTypeLabel') })
      })
      ;(res.results?.bundle ?? []).forEach(r => {
        items.push({
          id: r.object_id,
          resultType: 'bundle',
          displayName: r.display?.name ?? t('ui.bundleNumber', { id: r.object_id }),
          sub: r.display?.subtitle || null,
          typeLabel: t('entity.bundle'),
        })
      })
      searchResults.value = items
    } catch {
      searchResults.value = []
    } finally {
      searchLoading.value = false
    }
  }, 280)
})

function onAddBlur() {
  setTimeout(() => { searchOpen.value = false }, 150)
}

async function addFromSearch(result) {
  searchOpen.value    = false
  addItemError.value  = null
  addingItem.value    = true
  addItemQuery.value  = ''
  searchResults.value = []
  const sid = activeSectionId.value
  try {
    if (result.resultType === 'bundle') {
      store.addCheckoutBundle({ id: result.id, name: result.displayName })
      router.push({ path: '/checkout/', query: { project: projectId.value, plan: '1' } })
    } else if (result.resultType === 'item_metadata') {
      await addItemToProjectById(projectId.value, result.id, { section_id: sid })
      await load()
      toast.success(`${t('projectItems.added')} ${result.displayName}`)
      await checkOpenEndedWarning(result.id, result.displayName)
    } else {
      await addUnresolvedMultiItemToProject(projectId.value, result.id, 1, { section_id: sid })
      await load()
      toast.success(`${t('projectItems.added')} ${result.displayName}`)
    }
  } catch (e) {
    addItemError.value = result.resultType === 'bundle'
      ? parseBundleCheckoutError(e)
      : (e?.data?.error ?? e?.message ?? t('projectItems.addFailed'))
  } finally {
    addingItem.value = false
    await nextTick()
    addItemInput.value?.focus()
  }
}

async function onAddItem() {
  searchOpen.value = false
  const barcode = addItemQuery.value.trim()
  if (!barcode || addingItem.value) return
  addingItem.value   = true
  addItemError.value = null
  const sid = activeSectionId.value
  try {
    const { pick_list_item, conflicts } = await addAdhocItem(projectId.value, { barcode })
    pick_list_item.section      = sid ?? null
    pick_list_item.section_name = sid != null ? (sections.value.find(s => s.id === sid)?.name ?? null) : null
    projectItems.value.push(pick_list_item)
    addItemQuery.value = ''
    if (conflicts?.length) {
      toast.warning(t('itemMetadata.conflictProject', { names: conflicts.map(c => c.project_name).join(', ') }))
    }
  } catch (e) {
    addItemError.value = e?.data?.error ?? e?.message ?? t('projectItems.addFailed')
  } finally {
    addingItem.value = false
    await nextTick()
    addItemInput.value?.focus()
  }
}

async function onRemoveItem(item) {
  item._removing = true
  try {
    await removeProjectItem(item.id)
    projectItems.value = projectItems.value.filter(i => i.id !== item.id)
    toast.success(t('projectItems.itemRemoved'))
  } catch {
    item._removing = false
    toast.error(t('projectItems.itemRemoveFailed'))
  }
}

// ─── navigation ────────────────────────────────────────────────────────────────
function onNavigateItem(item) {
  if (item.item_metadata_id && item.item?.item_type === 2) {
    router.push(`/instance/${item.item_metadata_id}`)
  } else if (item.item?.id) {
    router.push(`/item/${item.item.id}`)
  }
}

function goToPick() {
  router.push(`/project/${projectId.value}/pick`)
}

function browseAndAdd() {
  store.enableAddToProject({
    id:         parseInt(projectId.value, 10),
    name:       projectName.value,
    start_time: projectStartTime.value,
    end_time:   projectEndTime.value,
  })
  router.push('/items')
}

function browseBundles() {
  store.enableAddToProject({
    id:         parseInt(projectId.value, 10),
    name:       projectName.value,
    start_time: projectStartTime.value,
    end_time:   projectEndTime.value,
  })
  router.push('/bundles')
}

onMounted(load)
</script>
